import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User as FirebaseUser, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { User, COLLECTIONS } from '../lib/firestore-schema';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userData: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, userData: Partial<User>) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (userData: Partial<User>) => Promise<void>;
  updateUserPassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const login = async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await fetchUserData(userCredential.user.uid);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string, userData: Partial<User>) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update Firebase Auth profile
      await updateProfile(user, {
        displayName: `${userData.firstName} ${userData.lastName}`,
      });

      // Create user document in Firestore
      const newUserData: User = {
        id: user.uid,
        email: user.email!,
        phoneNumber: userData.phoneNumber || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        role: userData.role || 'member',
        profilePicture: userData.profilePicture || '',
        dateOfBirth: userData.dateOfBirth || '',
        gender: userData.gender || undefined,
        nationalId: userData.nationalId || '',
        address: userData.address || {
          street: '',
          city: '',
          county: '',
          country: 'Kenya',
          postalCode: '',
        },
        language: userData.language || 'en',
        isActive: true,
        lastLogin: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await setDoc(doc(db, COLLECTIONS.USERS, user.uid), newUserData);
      setUserData(newUserData);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUserData(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  const updateUserProfile = async (updatedData: Partial<User>) => {
    if (!currentUser || !userData) {
      throw new Error('No user logged in');
    }

    try {
      const userRef = doc(db, COLLECTIONS.USERS, currentUser.uid);
      const updateData = {
        ...updatedData,
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(userRef, updateData);
      
      // Update Firebase Auth profile if name changed
      if (updatedData.firstName || updatedData.lastName) {
        await updateProfile(currentUser, {
          displayName: `${updatedData.firstName || userData.firstName} ${updatedData.lastName || userData.lastName}`,
        });
      }

      setUserData(prev => prev ? { ...prev, ...updateData } : null);
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  const updateUserPassword = async (newPassword: string) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    try {
      await updatePassword(currentUser, newPassword);
    } catch (error) {
      console.error('Password update error:', error);
      throw error;
    }
  };

  const fetchUserData = async (uid: string) => {
    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, uid));
      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        setUserData(userData);
        
        // Update last login
        await updateDoc(doc(db, COLLECTIONS.USERS, uid), {
          lastLogin: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('Fetch user data error:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        await fetchUserData(user.uid);
      } else {
        setUserData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    userData,
    loading,
    login,
    register,
    logout,
    resetPassword,
    updateUserProfile,
    updateUserPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
